<template>
  <div class="forecast">
    <div
      :aria-label="$t('web.widgets.weather.forecast.title')"
      class="forecast__tabs"
      role="tablist"
    >
      <button
        id="weather-forecast-tab-daily"
        :class="{ 'forecast__tab--active': isDaily }"
        :aria-selected="isDaily.toString()"
        aria-controls="weather-forecast-daily"
        class="forecast__tab"
        role="tab"
        data-test-id="forecast-tab-daily"
        @click="showDaily"
      >
        {{ $t('web.widgets.weather.daily') }}
      </button>
      <button
        id="weather-forecast-tab-hourly"
        :class="{ 'forecast__tab--active': isHourly }"
        :aria-selected="isHourly.toString()"
        aria-controls="weather-forecast-hourly"
        class="forecast__tab"
        role="tab"
        data-test-id="forecast-tab-hourly"
        @click="showHourly"
      >
        {{ $t('web.widgets.weather.hourly') }}
      </button>
    </div>
    <EHourlyForecast
      id="weather-forecast-hourly"
      :class="{
        'forecast__tabpanel': true,
        'forecast__tabpanel--visible': hourlyForecastShown,
      }"
      :aria-hidden="!hourlyForecastShown"
      :forecast="hourlyForecast"
      :units="units"
      :is-mobile="isMobile"
      :sunrise="sunrise"
      :sunset="sunset"
      role="tabpanel"
      aria-labelledby="weather-forecast-tab-hourly"
      data-test-id="forecast-hourly"
    />
    <EDailyForecast
      id="weather-forecast-daily"
      :class="{
        'forecast__tabpanel': true,
        'forecast__tabpanel--visible': dailyForecastShown,
      }"
      :aria-hidden="!dailyForecastShown"
      :forecast="dailyForecast"
      :units="units"
      :is-mobile="isMobile"
      :selected="selected"
      role="tabpanel"
      aria-labelledby="weather-forecast-tab-daily"
      data-test-id="forecast-daily"
      @select="onDailySelect"
      @expand-daily="onDailyExpand"
    />
  </div>
</template>

<script>
import { trackClick, TYPE_WIDGET } from '~~/search/common/js/client/analytics/rich-content';
import { isUnitSystem } from '~~/common/js/universal/validators';
import EDailyForecast from './daily';
import EHourlyForecast from './hourly';

const DAILY = 'DAILY';
const HOURLY = 'HOURLY';

export const TRACK_ID = 'weather';

export default {
  name: 'EForecast',
  components: {
    EDailyForecast,
    EHourlyForecast,
  },
  props: {
    dailyForecast: {
      type: Array,
      required: true,
    },
    hourlyForecast: {
      type: Array,
      required: true,
    },
    units: {
      type: String,
      required: true,
      validator: isUnitSystem,
    },
    isMobile: {
      type: Boolean,
      required: true,
    },
    selected: {
      type: Number,
      required: true,
    },
    sunrise: {
      type: Date,
      required: true,
    },
    sunset: {
      type: Date,
      required: true,
    },
  },
  data() {
    return {
      tab: DAILY,
    };
  },
  computed: {
    isDaily() {
      return this.tab === DAILY;
    },
    isHourly() {
      return this.tab === HOURLY;
    },
    dailyForecastShown() {
      return this.isMobile || this.isDaily;
    },
    hourlyForecastShown() {
      return this.isMobile || this.isHourly;
    },
  },
  methods: {
    track(target, property = '') {
      trackClick({
        name: TRACK_ID,
        type: TYPE_WIDGET,
        target,
        property,
      })();
    },
    showDaily() {
      this.tab = DAILY;
      this.track('daily');
    },
    showHourly() {
      this.tab = HOURLY;
      this.track('hourly');
    },
    onDailySelect(day) {
      this.track('day_select', `${day}`);
      this.$emit('select', day);
    },
    onDailyExpand() {
      this.$emit('expand-daily');
      this.track('weekly');
    },
  },
};
</script>

<style lang="scss" scoped>
.forecast__tabs {
  display: none;

  @include tablet {
    display: flex;
    color: var(--color-text-secondary);
    margin: $space-m 0 0 0;
  }
}

.forecast__tab {
  appearance: none;
  border: none;
  background: inherit;
  color: inherit;
  cursor: pointer;
  padding: 0;
  line-height: inherit;
  margin-right: $space-s;
  transition-property: color, border-bottom-color;
  transition-duration: $timing-2s;
  transition-timing-function: $easing;
  border-bottom: $space-2s solid transparent;
  white-space: nowrap;
}

.forecast__tab:hover,
.forecast__tab:focus {
  border-bottom-color: var(--color-text-primary);
  color: var(--color-text-primary);
}

.forecast__tab.forecast__tab--active {
  border-bottom-color: var(--color-brand-primary);
  color: var(--color-brand-primary);
  font-weight: bold;
}

.forecast__tabpanel {
  height: 0;
  overflow: visible;
  position: absolute;

  @include tablet {
    overflow: hidden;
  }
}

.forecast__tabpanel--visible {
  height: auto;
  position: static;
  overflow: visible;
}
</style>
